/**
 * OpenBoardList — the public "Open Board" on the Arena tab: takeable challenges from strangers.
 *
 * Owns the browse state (rank floor / entry band / challenge size / sort) and applies it to the page
 * the server returned, so a chip tap re-renders instantly. Distinguishes loading / error / empty
 * explicitly, and separates "nobody has posted" from "your filters hid everything" — the second
 * one is recoverable and says so. specs/open-challenges AC-6/AC-17.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  View, Text, ActivityIndicator, Pressable,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../../src/theme/useTheme';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useOpenBoardQuery } from '../../../src/challenges/hooks/useOpenBoardQuery';
import { OpenChallengeCard } from '../OpenChallengeCard';
import { OpenBoardFilterBar } from '../OpenBoardFilterBar';
import { OpenBoardFilterSheet } from '../OpenBoardFilterSheet';
import { OpenBoardTakeOn } from '../OpenBoardTakeOn';
import { ArenaEmptyStateMonkey } from '../ArenaEmptyStateMonkey/ArenaEmptyStateMonkey';
import {
  OPEN_BOARD_DEFAULT_BROWSE,
  OPEN_BOARD_TEST_IDS,
} from '../../../src/challenges/constants/openBoard.constants';
import {
  browseOpenBoard,
  countActiveFilters,
} from '../../../src/challenges/helpers/openBoardBrowse.helper';
import type {
  OpenBoardBrowseState,
  OpenBoardCard,
  OpenBoardFilterSection,
} from '../../../src/challenges/types/openBoard.types';
import { getOpenBoardListStyles } from './OpenBoardList.styles';

export const OpenBoardList: React.FC = () => {
  const theme = useTheme();
  const styles = useMemo(() => getOpenBoardListStyles(theme), [theme]);
  const { userId } = useSupabaseAuth();
  const queryClient = useQueryClient();

  const [browse, setBrowse] = useState<OpenBoardBrowseState>(OPEN_BOARD_DEFAULT_BROWSE);
  // `sheetSection` is both "is the sheet open" and "which chip opened it": undefined = closed,
  // null = the funnel button, a section = that chip.
  const [sheetSection, setSheetSection] = useState<OpenBoardFilterSection | null | undefined>();

  const {
    data, isLoading, isError, refetch, isRefetching,
  } = useOpenBoardQuery();

  const cards = useMemo(() => data ?? [], [data]);
  const visible = useMemo(() => browseOpenBoard(cards, browse), [cards, browse]);
  const activeFilters = countActiveFilters(browse);

  // Take on opens the app's existing picks modal in place. It deliberately does NOT navigate to
  // the challenge detail screen: that screen is read-only, and for an un-taken open challenge the
  // pick-seal empties it, so it offered a taker nothing to press.
  const [takeOnId, setTakeOnId] = useState<string | null>(null);

  const handleTakenOn = useCallback(() => {
    setTakeOnId(null);
    // Taking one on makes the viewer a participant, so it is not just this board that went stale —
    // the Arena's own lists ("My Challenges", history, the detail cache) all now answer "what am I
    // in?" incorrectly. Refetching only the board left the accepted challenge invisible on the
    // other tab until a manual page refresh.
    // The prefix covers every challenges key, including this board's ('challenges','open-board',…),
    // so it replaces the narrower refetch rather than running alongside it.
    void queryClient.invalidateQueries({ queryKey: ['challenges'] });
  }, [queryClient]);

  const clearFilters = useCallback(
    () => setBrowse((prev) => ({ ...OPEN_BOARD_DEFAULT_BROWSE, sort: prev.sort })),
    []
  );

  const renderItem = useCallback(
    ({ item }: { item: OpenBoardCard }) => (
      <OpenChallengeCard card={item} onTakeOn={setTakeOnId} />
    ),
    []
  );

  const renderSeparator = useCallback(() => <View style={styles.separator} />, [styles.separator]);

  if (isLoading) {
    return (
      <View style={styles.centered} testID="open-board-loading">
        <ActivityIndicator color={theme.primary} />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={styles.centered} testID="open-board-error">
        <Text style={styles.errorTitle}>Couldn&apos;t load the open board</Text>
        <Text style={styles.emptyBody}>Pull to try again.</Text>
      </View>
    );
  }

  // Nothing on the board at all — filters are not the cause, so do not offer to clear them.
  if (cards.length === 0) {
    return (
      <View style={styles.centered} testID="open-board-empty">
        <ArenaEmptyStateMonkey />
        <Text style={styles.emptyTitle}>No open challenges right now</Text>
        <Text style={styles.emptyBody}>
          Post one for anyone to take, or check back soon.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container} testID="open-board-list">
      <OpenBoardFilterBar
        browse={browse}
        resultCount={visible.length}
        onOpenSheet={setSheetSection}
        onChange={setBrowse}
      />

      {visible.length === 0 ? (
        <View style={styles.centered} testID="open-board-filtered-empty">
          <ArenaEmptyStateMonkey />
          <Text style={styles.emptyTitle}>No challenges match those filters.</Text>
          {activeFilters > 0 && (
            <Pressable
              onPress={clearFilters}
              testID={OPEN_BOARD_TEST_IDS.clearFilters}
              accessibilityRole="button"
              style={({ pressed }) => [styles.clearButton, pressed && styles.clearButtonPressed]}
            >
              <Text style={styles.clearButtonText}>Clear Filters</Text>
            </Pressable>
          )}
        </View>
      ) : (
        <FlashList
          data={visible}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          ItemSeparatorComponent={renderSeparator}
          onRefresh={() => { void refetch(); }}
          refreshing={isRefetching}
          showsVerticalScrollIndicator={false}
        />
      )}

      <OpenBoardFilterSheet
        visible={sheetSection !== undefined}
        browse={browse}
        resultCount={visible.length}
        focusSection={sheetSection ?? null}
        onChange={setBrowse}
        onClose={() => setSheetSection(undefined)}
      />

      {userId != null && (
        <OpenBoardTakeOn
          challengeId={takeOnId}
          currentUserId={userId}
          onClose={() => setTakeOnId(null)}
          onTakenOn={handleTakenOn}
        />
      )}
    </View>
  );
};
